// @ts-strict-ignore
import { useAtom, useAtomValue, useSetAtom } from 'jotai'
import { usePutAcceptQuoteMutation } from 'hooks/mutations/usePutAcceptQuoteMutation'
import { useRefreshConvertQuoteMutation } from 'hooks/mutations/useRefreshConvertQuoteMutation'
import { useMappedErrorMessage } from 'hooks/useMappedErrorMessage'
import { BaseErrorResponse } from 'types/beneficiaries'
import { ConvertStep } from 'types/walletActions'
import {
  AmplitudeEvent,
  AmplitudeEventAction,
  AmplitudeEventCategory,
} from 'utils/amplitude/amplitudeEvents'
import { logSentryErrorAndGetTraceId } from 'utils/sentry'
import track from 'utils/tracker'
import {
  convertFormValuesAtom,
  convertFromWalletAtom,
  convertStepAtom,
  convertToWalletAtom,
  quoteAtom,
} from '../atoms/atoms'
import { getQuotePayload } from '../utils'

export const useConvertReview = () => {
  const setConvertStep = useSetAtom(convertStepAtom)
  const convertToWallet = useAtomValue(convertToWalletAtom)
  const convertFromWallet = useAtomValue(convertFromWalletAtom)
  const convertFormValues = useAtomValue(convertFormValuesAtom)
  const [quote, setQuote] = useAtom(quoteAtom)

  const amplitudeEventOptions = {
    category: AmplitudeEventCategory.MERCHANT_PORTAL,
    action: AmplitudeEventAction.CLICK,
    fromCurrency: convertFromWallet?.currency?.code?.toLowerCase(),
    toCurrency: convertToWallet?.currency?.code?.toLowerCase(),
  }

  // API Requests
  const { requestError, resetRequestError, setRequestError } = useMappedErrorMessage()
  const { mutate: refreshQuote, isPending: isRefreshingQuote } = useRefreshConvertQuoteMutation()
  const { mutate: acceptQuote, isPending: isAcceptingQuote } = usePutAcceptQuoteMutation()

  // Helper functions
  const handleRefreshQuote = () => {
    const data = getQuotePayload(
      convertFromWallet,
      convertToWallet,
      convertFormValues,
      convertFormValues?.changedField
    )
    if (!data) return
    resetRequestError()

    refreshQuote(
      { transferId: quote?.uuid, payload: data },
      {
        onSuccess: response => {
          track.Amp.track(AmplitudeEvent.CONVERT_REFRESH_QUOTE, amplitudeEventOptions)
          setQuote(response)
        },
        onError: (error: BaseErrorResponse) => {
          track.Amp.track(AmplitudeEvent.EOF_CONVERT_REFRESH_QUOTE_ERROR, amplitudeEventOptions)
          const sentryTraceId = logSentryErrorAndGetTraceId('convertQuote', error)
          setRequestError({
            errorCode: error?.status,
            handledMessage: error?.data?.errorList?.[0]?.message ?? error?.data?.message,
            ErrorMessages: error?.data?.details?.errors,
            sentryTraceId,
            show: true,
          })
        },
      }
    )
  }

  const handleAcceptQuote = () => {
    resetRequestError()
    acceptQuote(
      { quoteId: quote?.uuid, digest: quote?.digest },
      {
        onSuccess: () => {
          track.Amp.track(AmplitudeEvent.CONVERT_ACCEPT_QUOTE, amplitudeEventOptions)
          setConvertStep(ConvertStep.COMPLETE)
        },
        onError: (error: BaseErrorResponse) => {
          track.Amp.track(AmplitudeEvent.EOF_CONVERT_ACCEPT_QUOTE_ERROR, amplitudeEventOptions)
          const sentryTraceId = logSentryErrorAndGetTraceId('acceptQuote', error)
          setRequestError({
            errorCode: error?.status,
            handledMessage: error?.data?.errorList?.[0]?.message ?? error?.data?.message,
            ErrorMessages: error?.data?.details?.errors,
            sentryTraceId,
            show: true,
          })
        },
      }
    )
  }

  return {
    requestError,
    isRefreshingQuote,
    isAcceptingQuote,
    handleRefreshQuote,
    handleAcceptQuote,
  }
}
